import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { BlockRender } from "@/pages/admin/AdminBlog";

export default function BlogPost() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  const [c, setC] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/blog/posts/${slug}`).then(({ data }) => setState({ loading: false, ...data })).catch(() => setState({ loading: false, error: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async () => {
    if (!c.name.trim() || !c.message.trim()) return toast.error("Name and comment are required");
    setSending(true);
    try { const { data } = await api.post(`/blog/posts/${slug}/comments`, c); toast.success(data.message); setC({ name: "", email: "", message: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  if (state.loading) return <div className="min-h-screen grid place-items-center bg-background"><Icon name="Loader2" className="h-8 w-8 animate-spin text-primary" /></div>;
  if (state.error) return <div className="min-h-screen bg-background"><PublicHeader /><div className="py-24 text-center"><Icon name="Newspaper" className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-3 font-semibold">Article not found</p><Link to="/blog" className="text-primary text-sm font-semibold mt-2 inline-block">← Back to blog</Link></div></div>;

  const { post, comments } = state;
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <article className="max-w-2xl mx-auto px-4 py-12" data-testid="blog-post">
        <Link to="/blog" className="text-sm font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6"><Icon name="ArrowLeft" className="h-4 w-4" />All articles</Link>
        {post.category && <span className="text-xs font-semibold text-primary">{post.category}</span>}
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold mt-2">{post.title}</h1>
        <div className="text-sm text-muted-foreground mt-3">{post.author} · {post.publish_date ? new Date(post.publish_date).toLocaleDateString() : ""} · {post.views || 0} views</div>
        {post.featured_image && <img src={post.featured_image} alt="" className="w-full rounded-2xl mt-6" />}
        {post.excerpt && <p className="text-lg text-muted-foreground mt-6">{post.excerpt}</p>}
        <div className="mt-6"><BlockRender blocks={post.content || []} /></div>
        {(post.tags || []).length > 0 && <div className="flex flex-wrap gap-2 mt-8">{post.tags.map((t) => <span key={t} className="text-xs rounded-full bg-muted px-3 py-1">#{t}</span>)}</div>}

        <div className="mt-12 pt-8 border-t border-border" data-testid="blog-comments">
          <h2 className="text-xl font-display font-bold">Comments ({comments.length})</h2>
          <div className="space-y-4 mt-4">
            {comments.length === 0 && <p className="text-sm text-muted-foreground">Be the first to comment.</p>}
            {comments.map((cm, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4"><div className="text-sm font-semibold">{cm.name}</div><p className="text-sm text-foreground/90 mt-1 whitespace-pre-wrap">{cm.message}</p><div className="text-xs text-muted-foreground mt-1">{new Date(cm.created_at).toLocaleDateString()}</div></div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="comment-form">
            <h3 className="font-display font-bold">Leave a comment</h3>
            <p className="text-xs text-muted-foreground">Comments are reviewed before appearing.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="comment-name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} placeholder="Your name" className="rounded-xl border border-input bg-background px-3 py-2 text-sm" />
              <input data-testid="comment-email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} placeholder="Email (optional)" className="rounded-xl border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <textarea data-testid="comment-message" value={c.message} onChange={(e) => setC({ ...c, message: e.target.value })} rows={3} placeholder="Your comment" className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm" />
            <button data-testid="comment-submit" onClick={submit} disabled={sending} className="rounded-xl bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60">{sending ? "Sending…" : "Post comment"}</button>
          </div>
        </div>
      </article>
      <PublicFooter />
    </div>
  );
}